import type { ReactNode } from "react";
import { ChevronDown, RotateCcw, X } from "lucide-react";
import { HEADING_WEIGHTS, RADIUS_PRESETS, type SectionNode, type SectionTheme as Theme, type WebsiteDefinition } from "@baze/schema";
import { fontStack } from "@baze/engine";
import { Segmented } from "@/components/ui/segmented";
import { t } from "@/i18n/id";
import type { themeFor } from "@/lib/catalog";
import { updateSectionStyle, updateTextStyle } from "../model/ops";
import { ColorField } from "./ColorField";
import { FontPairs, RadiusPreview, Tile, nearestWeight, siteSwatches } from "./StylePanel";
import { textLabel } from "./TextToolbar";

type Apply = (next: WebsiteDefinition, key?: string) => void;
type AppTheme = NonNullable<ReturnType<typeof themeFor>>;

/**
 * "Gaya section": the Gaya tab, for one section. Everything starts from the site's look; each control
 * shows the site's value until changed, and "Kembalikan ke gaya situs" drops them all.
 */
export function SectionThemeGroup({ def, section, theme, apply }: { def: WebsiteDefinition; section: SectionNode; theme: AppTheme; apply: Apply }) {
  const own: Theme = section.style?.theme ?? {};
  const changed = Object.keys(own).length;
  const c = theme.tokens.colors;
  const set = (patch: Partial<Theme>, key?: string) => {
    const next: Record<string, unknown> = { ...own, ...patch };
    for (const k of Object.keys(next)) if (next[k] === undefined) delete next[k];
    apply(updateSectionStyle(def, section.id, { theme: Object.keys(next).length ? (next as Theme) : undefined }), key && `section-theme:${section.id}:${key}`);
  };
  const sitePair = def.theme.overrides?.fontPair ?? theme.design.fontPair;
  const heading = own.headingWeight ?? nearestWeight(theme.tokens.heading.weight);
  const pairFont = (id: string | undefined) => theme.library.fonts.find((f) => f.id === theme.library.pairs.find((p) => p.id === id)?.heading);
  const headingFont = fontStack(pairFont(own.fontPair) ?? theme.fonts.heading);

  return (
    <details className="group/st border-t border-line" open={changed > 0 || undefined}>
      <summary className="flex cursor-pointer list-none items-center gap-2.5 py-3 select-none [&::-webkit-details-marker]:hidden">
        <span className="flex -space-x-1.5" aria-hidden="true">
          {[own.primary ?? c.primary, own.accent ?? c.accent].map((hex, i) => (
            <span key={i} className="size-4 rounded-full ring-2 ring-white" style={{ background: hex }} />
          ))}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[13px] font-semibold text-slate-800">{t.editor.style.sectionTheme.title}</span>
          <span className="block truncate text-[11.5px] text-slate-500">{changed ? `${changed} ${t.editor.style.sectionTheme.changedSuffix}` : t.editor.style.sectionTheme.hint}</span>
        </span>
        <ChevronDown className="size-4 text-slate-400 transition-transform group-open/st:rotate-180" aria-hidden="true" />
      </summary>
      <div className="space-y-4 border-t border-line pt-3 pb-1">
        <div className="grid gap-2">
          <ColorField
            size="chip"
            label={t.editor.style.colors.primary}
            value={own.primary ?? c.primary}
            overridden={!!own.primary}
            onReset={() => set({ primary: undefined })}
            swatches={siteSwatches(theme)}
            onChange={(hex) => set({ primary: hex }, "primary")}
          />
          <ColorField
            size="chip"
            label={t.editor.style.colors.accent}
            value={own.accent ?? c.accent}
            overridden={!!own.accent}
            onReset={() => set({ accent: undefined })}
            swatches={siteSwatches(theme)}
            onChange={(hex) => set({ accent: hex }, "accent")}
          />
        </div>

        <Sub label={t.editor.style.sectionTheme.fonts}>
          <FontPairs pairs={theme.library.pairs} fonts={theme.library.fonts} current={own.fontPair ?? sitePair} designPair={sitePair} badge={t.editor.style.sectionTheme.followSite} onPick={(id) => set({ fontPair: id === sitePair ? undefined : id })} />
        </Sub>

        <Sub label={t.editor.style.sectionTheme.headings}>
          <div className="grid gap-2">
            <Segmented
              size="sm"
              full
              label={t.editor.style.fonts.weight}
              value={String(heading)}
              onChange={(v) => set({ headingWeight: Number(v) === nearestWeight(theme.tokens.heading.weight) ? undefined : Number(v) })}
              options={HEADING_WEIGHTS.map((w) => ({ value: String(w), ariaLabel: String(w), label: <span style={{ fontFamily: headingFont, fontWeight: w }}>Ag</span> }))}
            />
            <Segmented
              size="sm"
              full
              label={t.editor.style.fonts.case}
              value={own.headingCase ?? theme.tokens.heading.case}
              onChange={(v) => set({ headingCase: v === theme.tokens.heading.case ? undefined : v })}
              options={[
                { value: "normal", label: "Aa", ariaLabel: t.editor.style.fonts.caseNormal },
                { value: "uppercase", label: "AA", ariaLabel: t.editor.style.fonts.caseUpper },
              ]}
            />
          </div>
        </Sub>

        <Sub label={t.editor.style.radius.title}>
          <div className="grid grid-cols-4 gap-2">
            {RADIUS_PRESETS.map((r) => (
              <Tile key={r} active={own.radius === r} onClick={() => set({ radius: own.radius === r ? undefined : r })} label={t.editor.style.radius.presets[r]}>
                <RadiusPreview preset={r} color={own.primary ?? c.primary} />
              </Tile>
            ))}
          </div>
        </Sub>

        {changed ? (
          <button type="button" onClick={() => set({ primary: undefined, accent: undefined, fontPair: undefined, headingWeight: undefined, headingCase: undefined, headingTracking: undefined, radius: undefined })} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-slate-500 hover:text-slate-900">
            <RotateCcw className="size-3.5" aria-hidden="true" />
            {t.editor.style.sectionTheme.reset}
          </button>
        ) : null}
      </div>
    </details>
  );
}

/** The section's styled texts (set from the canvas toolbar), each removable; a hint when there are none. */
export function TextStyleList({ def, section, apply }: { def: WebsiteDefinition; section: SectionNode; apply: Apply }) {
  const keys = Object.keys(section.style?.texts ?? {});
  return (
    <div className="space-y-2">
      <p className="text-[12px] font-semibold text-slate-600">{t.editor.style.textList.title}</p>
      {keys.length ? (
        <ul className="flex flex-wrap gap-1.5">
          {keys.map((k) => (
            <li key={k} className="inline-flex h-7 items-center gap-1 rounded-full bg-slate-100 pr-1 pl-2.5 text-[12px] font-medium text-slate-700">
              {textLabel(k)}
              <button
                type="button"
                onClick={() => apply(updateTextStyle(def, section.id, k, null))}
                aria-label={t.editor.style.textList.remove(textLabel(k))}
                className="grid size-5 place-items-center rounded-full text-slate-400 hover:bg-white hover:text-slate-900"
              >
                <X className="size-3" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[12.5px] leading-snug text-slate-500">{t.editor.style.textList.empty}</p>
      )}
    </div>
  );
}

function Sub({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <p className="text-[11px] font-bold tracking-wide text-slate-400 uppercase">{label}</p>
      {children}
    </div>
  );
}
